import { useState, useRef, useEffect } from 'react'
import { useUIStore } from '../store/uiStore.js'
import { colorFor } from '../data/userColors.js'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * NewGroupDmButton — small "+ New group" button that opens a
 * multi-select popover to pick 2+ teammates. On "Start", opens the DM
 * panel with all selected participants.
 *
 * The DM panel + useDms hook already support any number of
 * participants — they key threads by a sorted-join of names — so we
 * don't need any new data plumbing for groups beyond this picker.
 *
 * Supports 1-on-1 selection too (pick one person, start DM). This is
 * also handy because the PresenceBar only lists recently-active
 * teammates; this picker shows the whole team.
 */
export default function NewGroupDmButton() {
  const currentUser = useUIStore((s) => s.currentUser)
  const openDmWith = useUIStore((s) => s.openDmWith)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const popRef = useRef(null)

  // Reset selection whenever we re-open
  useEffect(() => {
    if (open) setSelected(new Set())
  }, [open])

  // Close on outside click + escape
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (popRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const handleStart = () => {
    if (selected.size === 0) return
    openDmWith([...selected])
    setOpen(false)
  }

  const candidates = TEAM.filter((n) => n !== currentUser)

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        data-tour="new-dm-button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((x) => !x)
        }}
        title="Start a new direct message or group chat"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 10px',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--accent)',
          background: open ? 'var(--accent-bg, #eff6ff)' : '#fff',
          border: '1px dashed #bfdbfe',
          borderRadius: 99,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        ＋ New DM
      </button>

      {open && (
        <div
          ref={popRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 260,
            maxHeight: 380,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            zIndex: 950,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
              fontSize: 11,
              color: 'var(--muted)',
              fontWeight: 600,
            }}
          >
            Pick 1 person for a direct message, or 2+ for a group chat.
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 6,
            }}
          >
            {candidates.map((name) => {
              const c = colorFor(name)
              const isSelected = selected.has(name)
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggle(name)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    border: 'none',
                    background: isSelected ? c + '14' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 500,
                    color: 'var(--text)',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = '#f9fafb'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected ? c + '14' : 'transparent'
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: `1.5px solid ${isSelected ? c : '#d1d5db'}`,
                      background: isSelected ? c : '#fff',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: c,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      lineHeight: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {name[0]}
                  </span>
                  {name}
                </button>
              )
            })}
          </div>

          <div
            style={{
              padding: 10,
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#fafafa',
            }}
          >
            <span style={{ fontSize: 10, color: 'var(--muted)', flex: 1 }}>
              {selected.size === 0
                ? 'Select someone to message'
                : selected.size === 1
                  ? `Start DM with ${[...selected][0]}`
                  : `Start group with ${selected.size} people`}
            </span>
            <button
              type="button"
              onClick={handleStart}
              disabled={selected.size === 0}
              style={{
                padding: '5px 12px',
                border: 'none',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                background: selected.size > 0 ? 'var(--accent)' : '#e5e7eb',
                color: selected.size > 0 ? '#fff' : '#9ca3af',
                cursor: selected.size > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              Start
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
